import React from 'react';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { toast } from 'react-toastify';
import styles from '../css/vdr.module.css';

const splitParticipantTokens = raw =>
    String(raw || '')
        .split(/[,;\n]+/)
        .map(part => part.trim())
        .filter(Boolean);

const ParticipantsChipField = React.forwardRef(({ values = [], onChange, placeholder = 'Enter names or email addresses' }, ref) => {
    const [value, setValue] = React.useState('');
    const inputRef = React.useRef(null);

    const mergeTokens = (list, raw) => {
        const incoming = splitParticipantTokens(raw);
        if (!incoming.length && raw.trim()) {
            incoming.push(raw.trim());
        }
        if (!incoming.length) return list;
        const next = [...list];
        incoming.forEach(item => {
            const exists = next.some(existing => existing.toLowerCase() === item.toLowerCase());
            if (!exists) next.push(item);
        });
        return next;
    };

    const commit = raw => {
        const next = mergeTokens(values, raw);
        if (next.length !== values.length) onChange(next);
        setValue('');
    };

    React.useImperativeHandle(ref, () => ({
        flush: () => {
            if (!value.trim()) return values;
            const next = mergeTokens(values, value);
            if (next.length !== values.length) onChange(next);
            setValue('');
            return next;
        },
    }));

    const onInputChange = event => {
        const next = event.target.value;
        if (/[,;\n]$/.test(next)) {
            commit(next.slice(0, -1));
            return;
        }
        setValue(next);
    };

    const onKeyDown = event => {
        if (event.key === 'Enter' || event.key === 'Tab') {
            if (!value.trim()) return;
            event.preventDefault();
            commit(value);
        } else if (event.key === 'Backspace' && !value && values.length) {
            onChange(values.slice(0, -1));
        }
    };

    const onPaste = event => {
        const text = event.clipboardData?.getData('text');
        if (!text || !/[,;\n]/.test(text)) return;
        event.preventDefault();
        commit(`${value}${value ? ' ' : ''}${text}`);
    };

    const onBlur = () => {
        if (value.trim()) commit(value);
    };

    return (
        <div
            className={styles.chipBox}
            onClick={() => inputRef.current?.focus()}
            role="group"
            aria-label="Participants"
        >
            {values.map(item => (
                <span key={item} className={styles.chip}>
                    {item}
                    <button
                        type="button"
                        className={styles.chipRemove}
                        onClick={event => {
                            event.stopPropagation();
                            onChange(values.filter(entry => entry !== item));
                        }}
                        aria-label={`Remove ${item}`}
                    >
                        ×
                    </button>
                </span>
            ))}
            <input
                ref={inputRef}
                className={styles.chipInput}
                value={value}
                onChange={onInputChange}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                onBlur={onBlur}
                placeholder={values.length ? '' : placeholder}
                aria-label={placeholder}
                autoComplete="off"
            />
        </div>
    );
});

ParticipantsChipField.displayName = 'ParticipantsChipField';

const UploadRecordingModal = ({ open, onClose, onUpload }) => {
    const [file, setFile] = React.useState(null);
    const [transcriptFile, setTranscriptFile] = React.useState(null);
    const [title, setTitle] = React.useState('');
    const [meetingDate, setMeetingDate] = React.useState('');
    const [meetingTime, setMeetingTime] = React.useState('');
    const [source, setSource] = React.useState('MS Teams');
    const [meetingLink, setMeetingLink] = React.useState('');
    const [meetingLinkError, setMeetingLinkError] = React.useState('');
    const [participants, setParticipants] = React.useState([]);
    const [dragging, setDragging] = React.useState(false);
    const [draggingTranscript, setDraggingTranscript] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const inputRef = React.useRef(null);
    const transcriptRef = React.useRef(null);
    const participantsRef = React.useRef(null);

    React.useEffect(() => {
        if (open) {
            setFile(null);
            setTranscriptFile(null);
            setTitle('');
            setMeetingDate('');
            setMeetingTime('');
            setSource('MS Teams');
            setMeetingLink('');
            setMeetingLinkError('');
            setParticipants([]);
            setDragging(false);
            setDraggingTranscript(false);
            setSubmitting(false);
        }
    }, [open]);

    const isPlausibleUrl = value => {
        const trimmed = String(value || '').trim();
        if (!trimmed) return true;
        try {
            const parsed = new URL(trimmed);
            return parsed.protocol === 'http:' || parsed.protocol === 'https:';
        } catch {
            return false;
        }
    };

    if (!open) return null;

    const acceptRecording = list => {
        const next = list?.[0];
        if (next) setFile(next);
    };

    const acceptTranscript = list => {
        const next = list?.[0];
        if (next) setTranscriptFile(next);
    };

    const handleSubmit = async () => {
        if (!file) {
            toast.error('Select a recording file');
            return;
        }
        const trimmedLink = meetingLink.trim();
        if (trimmedLink && !isPlausibleUrl(trimmedLink)) {
            setMeetingLinkError('Enter a valid http or https URL');
            return;
        }
        setMeetingLinkError('');
        const body = new FormData();
        body.append('recording', file);
        body.append('title', title.trim() || file.name.replace(/\.[^.]+$/, ''));
        if (meetingDate) body.append('meeting_date', meetingDate);
        if (meetingTime) body.append('meeting_time', meetingTime);
        body.append('source', source);
        if (trimmedLink) body.append('meeting_link', trimmedLink);
        const participantList = participantsRef.current?.flush?.() ?? participants;
        if (participantList.length) body.append('participants', participantList.join(', '));
        if (transcriptFile) body.append('transcript', transcriptFile);
        setSubmitting(true);
        try {
            await onUpload?.(body);
            onClose();
        } catch (error) {
            const detail = error?.response?.data?.detail || error?.response?.data?.recording?.[0];
            toast.error(detail || 'Upload failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="upload-recording-title">
            <div className={`${styles.modal} ${styles.modalTall}`}>
                <div className={styles.modalHeader}>
                    <h2 id="upload-recording-title" className={styles.modalTitle}>
                        Upload meeting recording
                    </h2>
                    <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
                        <Dismiss20Regular />
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {/* Recording (required) */}
                    <label className={styles.fieldLabel}>
                        Recording <span className={styles.requiredMark}>*</span>
                    </label>
                    <div
                        className={`${styles.dropZone} ${styles.dropZoneDashed} ${dragging ? styles.dropZoneActive : ''}`}
                        onDragOver={e => {
                            e.preventDefault();
                            setDragging(true);
                        }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={e => {
                            e.preventDefault();
                            setDragging(false);
                            acceptRecording(e.dataTransfer.files);
                        }}
                        onClick={() => inputRef.current?.click()}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
                        }}
                    >
                        <p className={styles.dropZoneTitle}>{file ? file.name : 'Drag file here or choose file'}</p>
                        <p className={styles.dropZoneHint}>MP4, MOV, M4A, MP3, WAV supported</p>
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".mp4,.mov,.m4a,.mp3,.wav,audio/*,video/*"
                            style={{ display: 'none' }}
                            onChange={e => acceptRecording(e.target.files)}
                        />
                    </div>

                    {/* Meeting title */}
                    <label className={styles.fieldLabel}>Meeting title</label>
                    <input
                        className={styles.input}
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder="e.g. Technology diligence review"
                    />

                    {/* Meeting date + time */}
                    <div className={styles.fieldRow}>
                        <div className={styles.fieldCol}>
                            <label className={styles.fieldLabel}>Meeting date</label>
                            <input
                                type="date"
                                className={styles.input}
                                value={meetingDate}
                                onChange={e => setMeetingDate(e.target.value)}
                            />
                        </div>
                        <div className={styles.fieldCol}>
                            <label className={styles.fieldLabel}>Meeting time</label>
                            <input
                                type="time"
                                className={styles.input}
                                value={meetingTime}
                                onChange={e => setMeetingTime(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Source */}
                    <label className={styles.fieldLabel}>Source</label>
                    <div className={styles.selectWrap}>
                        <select className={styles.input} value={source} onChange={e => setSource(e.target.value)}>
                            <option>MS Teams</option>
                            <option>Plaud</option>
                            <option>Other (Zoom, Google Meet, In-person, etc.)</option>
                        </select>
                    </div>

                    {/* Meeting link (optional) */}
                    <label className={styles.fieldLabel} htmlFor="vdr-meeting-link">
                        Meeting link
                    </label>
                    <input
                        id="vdr-meeting-link"
                        type="url"
                        className={`${styles.input} ${meetingLinkError ? styles.inputError : ''}`}
                        value={meetingLink}
                        onChange={e => {
                            setMeetingLink(e.target.value);
                            if (meetingLinkError) setMeetingLinkError('');
                        }}
                        onBlur={() => {
                            const trimmed = meetingLink.trim();
                            if (trimmed && !isPlausibleUrl(trimmed)) {
                                setMeetingLinkError('Enter a valid http or https URL');
                            } else {
                                setMeetingLinkError('');
                            }
                        }}
                        placeholder="https://meet.example.com/abc-defg-hij"
                        autoComplete="url"
                    />
                    {meetingLinkError ? <p className={styles.fieldError}>{meetingLinkError}</p> : null}

                    {/* Participants */}
                    <label className={styles.fieldLabel}>Participants</label>
                    <ParticipantsChipField ref={participantsRef} values={participants} onChange={setParticipants} />

                    {/* Transcript optional */}
                    <label className={styles.fieldLabel}>Transcript (optional)</label>
                    <div
                        className={`${styles.dropZone} ${styles.dropZoneCompact} ${draggingTranscript ? styles.dropZoneActive : ''}`}
                        onDragOver={e => {
                            e.preventDefault();
                            setDraggingTranscript(true);
                        }}
                        onDragLeave={() => setDraggingTranscript(false)}
                        onDrop={e => {
                            e.preventDefault();
                            setDraggingTranscript(false);
                            acceptTranscript(e.dataTransfer.files);
                        }}
                        onClick={() => transcriptRef.current?.click()}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') transcriptRef.current?.click();
                        }}
                    >
                        {transcriptFile ? (
                            <p className={styles.dropZoneHint}>{transcriptFile.name}</p>
                        ) : (
                            <>
                                <p className={styles.dropZoneHint}>
                                    Leave blank if Lens should create a transcript from the recording.
                                </p>
                                <p className={styles.dropZoneHint}>Supported: TXT · DOCX · VTT · SRT</p>
                            </>
                        )}
                        <input
                            ref={transcriptRef}
                            type="file"
                            accept=".txt,.docx,.vtt,.srt,text/plain"
                            style={{ display: 'none' }}
                            onChange={e => acceptTranscript(e.target.files)}
                        />
                    </div>

                    {/* Destination banner */}
                    <div className={styles.destinationBanner}>
                        Destination: <strong>Meeting Recordings</strong> (project-level source collection)
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button type="button" className={styles.primaryBtn} onClick={handleSubmit} disabled={submitting}>
                        {submitting ? 'Uploading…' : 'Upload recording'}
                    </button>
                    <button type="button" className={styles.secondaryBtn} onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UploadRecordingModal;
